<script setup lang="ts">
import { useFishReconciliation } from "@/composables/tasks/fish-reconciliation/useFishReconciliation";
import { onMounted, ref, watch, computed } from "vue";
import FooterActions from "@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue";

const { activeTank, activeGroup, navigateToTankView, updateGroupProgressData } = useFishReconciliation();

const quantity = ref(0);
const comment = ref("");

const totalFishCount = computed(() =>
    activeGroup.value?.amountInTank || activeGroup.value?.count || 0
);

const totalEuthanized = computed(() => {
    if (!activeGroup.value?.progressData) return 0;

    if (Array.isArray(activeGroup.value.progressData)) {
        return activeGroup.value.progressData.reduce(
            (acc, item) => acc + (item.quantity || 0), 0
        );
    } else if (typeof activeGroup.value.progressData === 'object') {
        return activeGroup.value.progressData.quantity || 0;
    }

    return 0;
});

const euthanizationRate = computed(() => {
    if (totalFishCount.value === 0) return "0.00";
    const rate = (totalEuthanized.value / totalFishCount.value) * 100;
    return rate.toFixed(2);
});

const increaseQuantity = () => {
    if (quantity.value < totalFishCount.value) quantity.value++;
};

const decreaseQuantity = () => {
    if (quantity.value > 0) quantity.value--;
};

onMounted(() => {
    if (activeGroup.value?.progressData) {
        const progressData = activeGroup.value.progressData;
        if (typeof progressData === 'object') {
            quantity.value = progressData.quantity || 0;
            comment.value = progressData.comment || "";
        }
    }
});

watch(() => activeGroup.value?.progressData, (newProgressData) => {
    if (newProgressData) {
        quantity.value = newProgressData.quantity || 0;
        comment.value = newProgressData.comment || "";
    }
});

const handleSave = () => {
    const data = {
        quantity: quantity.value,
        comment: comment.value || undefined
    };

    updateGroupProgressData(data);

    if (activeTank.value) {
        navigateToTankView(activeTank.value.key);
    }
};

const handleBack = () => {
    if (activeTank.value) {
        navigateToTankView(activeTank.value.key);
    }
};
</script>

<template>
    <div class="h-full flex flex-col justify-between mt-5 gap-3 pb-5 mb-10">
        <div>
            <!-- Header -->
            <div class="flex gap-2 items-center pb-1 border-b border-secondary">
                <h1 class="py-2 text-3xl font-bold text-gray-700">
                    {{ activeTank?.label + " - " + activeGroup?.name }}
                </h1>
                <div class="flex items-center">
                    <span
                        v-for="flag of activeGroup?.tags"
                        :key="flag.title"
                        class="inline-flex mr-2 text-white text-xs p-1 rounded-md font-bold"
                        :style="{ backgroundColor: flag.backgroundColor }"
                    >
                        {{ flag.title }}
                    </span>
                </div>
            </div>

            <div class="flex flex-col gap-10 mt-10 relative">
                <!-- Quantity Input -->
                <div class="w-full md:w-1/2">
                    <h2 class="text-gray-700 text-xl font-bold">Fish euthanized:</h2>
                    <div class="flex items-center mt-3">
                        <button
                            class="px-4 py-2 border border-primary font-bold bg-primary text-white"
                            @click="decreaseQuantity"
                        >
                            -
                        </button>
                        <input
                            type="number"
                            v-model.number="quantity"
                            min="0"
                            :max="totalFishCount"
                            class="appearance-none w-full border border-primary px-4 py-2 text-center"
                        />
                        <button
                            class="px-4 py-2 border border-primary font-bold bg-primary text-white"
                            @click="increaseQuantity"
                        >
                            +
                        </button>
                    </div>

                    <!-- Statistics -->
                    <div class="flex flex-col gap-3 mt-5">
                        <strong>
                            Fish in tank:
                            <span class="text-primary">{{ totalFishCount }}</span>
                        </strong>
                        <strong>
                            Euthanization rate:
                            <span class="text-primary">{{ euthanizationRate }}%</span>
                        </strong>
                    </div>
                </div>

                <!-- Comment Section -->
                <div>
                    <h2 class="text-gray-700 text-xl font-bold">
                        Leave a comment (optional):
                    </h2>
                    <textarea
                        v-model="comment"
                        class="w-full mt-5 p-2 border border-secondary"
                        rows="6"
                    ></textarea>
                </div>
            </div>
        </div>

        <!-- Footer Actions -->
        <div class="mt-auto">
            <FooterActions>
                <template #start>
                    <button
                        @click="handleBack"
                        class="w-full"
                    >
                        Back
                    </button>
                </template>
                <template #end>
                    <button
                        @click="handleSave"
                        class="w-full"
                    >
                        Save
                    </button>
                </template>
            </FooterActions>
        </div>
    </div>
</template>
